import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function Footer() {
  const { t } = useTranslation();
  const [showContactModal, setShowContactModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setStatus('error');
      return;
    }

    try {
      const form = new FormData();
      form.append('name', formData.name);
      form.append('email', formData.email);
      form.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4kiu31086uolb65a2tg', {
        method: 'POST',
        body: new URLSearchParams(form as any),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => {
          setShowContactModal(false);
          setStatus('idle');
        }, 2000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <>
      <footer className="bg-gradient-to-br from-teal-50 to-emerald-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            {/* About Section */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">LinkQR</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t('footer.about')}
              </p>
            </div>

            {/* Features Section */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('footer.features')}</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-qr-code-line text-teal-600"></i>
                  {t('footer.feature1')}
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-link text-teal-600"></i>
                  {t('footer.feature2')}
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-pages-line text-teal-600"></i>
                  {t('footer.feature3')}
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-bar-chart-line text-teal-600"></i>
                  {t('footer.feature4')}
                </li>
              </ul>
            </div>

            {/* Contact Section */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('footer.contactTitle')}</h4>
              <button
                onClick={() => setShowContactModal(true)}
                className="bg-teal-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('footer.contact')}
              </button>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              © {new Date().getFullYear()} LinkQR. {t('footer.rights')}.
            </p>
          </div>
        </div>
      </footer>

      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('contact.title')}</h3>
            <p className="text-sm text-gray-600 mb-6">{t('contact.subtitle')}</p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.name')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.message')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500</p>
              </div>

              {status === 'success' && (
                <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm">
                  {t('contact.success')}
                </div>
              )}

              {status === 'error' && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm">
                  {t('contact.error')}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                {t('contact.submit')}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
